<template>
  <main class="call-logs-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索模型、账号或客户..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch"
          @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <el-button size="small" icon="el-icon-download" @click="exportLogs">导出调用记录</el-button>
      </div>
      <div class="desktop-only">
        <el-table :data="logs" size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
          <el-table-column v-if="showCustomerColumn" label="客户" width="160" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.customerName || '-' }}</template>
          </el-table-column>
          <el-table-column label="模型" min-width="220">
            <template slot-scope="scope">
              <div class="model-cell">
                <span class="model-icon">
                  <img v-if="scope.row.modelIcon" :src="scope.row.modelIcon" :alt="scope.row.model" @error="scope.row.modelIcon = ''">
                  <span v-else>{{ modelAbbr(scope.row.model) }}</span>
                </span>
                <span class="model-name">{{ scope.row.model }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="inputTokens" label="输入 token" width="110" />
          <el-table-column prop="cacheTokens" label="缓存命中" width="110" />
          <el-table-column prop="outputTokens" label="输出 token" width="110" />
          <el-table-column label="花费" width="120">
            <template slot-scope="scope">¥ {{ formatMoney(scope.row.totalCost) }}</template>
          </el-table-column>
          <el-table-column prop="responseStatus" label="状态码" width="90">
            <template slot-scope="scope">
              <el-tag size="small" :type="scope.row.responseStatus === 200 ? 'success' : (scope.row.responseStatus === 499 ? 'warning' : 'danger')">{{ scope.row.responseStatus }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="账号">
            <template slot-scope="scope">{{ formatAccount(scope.row) }}</template>
          </el-table-column>
          <el-table-column label="调用时间" width="170">
            <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mobile-only list-cards">
        <div class="list-card" v-for="row in logs" :key="row.id">
          <div class="card-header">
            <div class="model-cell" style="display:inline-flex;align-items:center;gap:8px;">
              <span class="model-icon" style="width:24px;height:24px;font-size:11px;">
                <img v-if="row.modelIcon" :src="row.modelIcon" :alt="row.model" @error="row.modelIcon = ''">
                <span v-else>{{ modelAbbr(row.model) }}</span>
              </span>
              <div>
                <div class="card-title">{{ row.model }}</div>
                <div class="card-sub" v-if="showCustomerColumn">{{ row.customerName || '-' }}</div>
              </div>
            </div>
            <el-tag size="small" :type="row.responseStatus === 200 ? 'success' : (row.responseStatus === 499 ? 'warning' : 'danger')">{{ row.responseStatus }}</el-tag>
          </div>
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">{{ row.inputTokens }}</div>
              <div class="stat-label">输入</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ row.cacheTokens }}</div>
              <div class="stat-label">缓存</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ row.outputTokens }}</div>
              <div class="stat-label">输出</div>
            </div>
          </div>
          <div class="meta-row">
            <span style="font-size:13px;font-weight:700;color:#34c98f;">¥ {{ formatMoney(row.totalCost) }}</span>
            <span>{{ formatAccount(row) }}</span>
            <span>{{ $formatTime(row.createdAt) }}</span>
          </div>
        </div>
      </div>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    </section>
  </main>
</template>

<script>
import * as XLSX from 'xlsx'
export default {
  name: 'CallLogsPage',
  data() {
    return {
      logs: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: ''
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    showCustomerColumn() {
      return this.logs.some(item => item.customerName || item.customerCode)
    }
  },
  mounted() {
    this.fetchLogs()
  },
  methods: {
    modelAbbr(name) {
      if (!name) return 'M'
      return name.substring(0, 2).toUpperCase()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchLogs()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchLogs()
    },
    formatAccount(row) {
      return row.accountName || row.accountUsername || row.accountId || '-'
    },
    formatMoney(value) {
      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 6
      }).format(value || 0)
    },
    async fetchLogs() {
      try {
        const res = await this.$http.get('/admin/call-logs', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.logs = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载调用日志失败')
      }
    },
    async exportLogs() {
      try {
        const res = await this.$http.get('/admin/call-logs', {
          params: {
            keyword: this.searchQuery || undefined,
            page: 1,
            pageSize: 1000
          }
        })
        const logs = res.records || []
        if (!logs.length) {
          this.$message.info('没有数据可导出')
          return
        }
        const data = logs.map((r, i) => ({
          '序号': i + 1,
          '模型': r.model,
          '输入 token': r.inputTokens,
          '缓存命中': r.cacheTokens,
          '输出 token': r.outputTokens,
          '花费': r.totalCost || 0,
          '状态码': r.responseStatus,
          '调用时间': this.$formatTime(r.createdAt)
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = [
          { wch: 6 }, { wch: 30 }, { wch: 12 },
          { wch: 12 }, { wch: 12 }, { wch: 14 },
          { wch: 8 }, { wch: 20 }
        ]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, '调用记录')
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const ts = new Date()
const pad = n => String(n).padStart(2, '0')
a.download = `调用记录_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败', e)
        this.$message.error('导出失败')
      }
    }
  }
}
</script>

<style scoped>
.call-logs-page {
  padding: 18px 24px 76px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.search-input {
  width: 220px;
}

.muted-text {
  font-size: 12px;
  color: var(--muted-2);
}

.model-cell {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.model-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  overflow: hidden;
  color: var(--cyan);
  font-size: 12px;
  font-weight: 800;
  background: rgba(79, 134, 255, 0.14);
  border: 1px solid rgba(133, 164, 255, 0.16);
}

.model-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.model-name {
  font-weight: 700;
  color: var(--text);
}

.list-cards { display: grid; gap: 14px; }

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .call-logs-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.stats-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.stat-item {
  flex: 1;
  min-width: 0;
  background: rgba(255,255,255,.04);
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
}
.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.stat-label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  margin-top: 2px;
  letter-spacing: .02em;
}
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,.35);
}
</style>
